import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import '../../ui/css/FunctionalStructure.css';

import { useFunctionalStructure } from 'hooks/services/useFunctionalStructure';
import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import { Panel } from 'primereact/panel';
import { Toast } from 'primereact/toast';
import { Tree, TreeExpandedEvent } from 'primereact/tree';
import { useSelector } from 'react-redux';

import {
  DepartmentsService,
  EmployeesService
} from '../../components/services';
import { GeographicalZoneService } from '../../components/services/GeographicalZoneService';
import { UsersTemplate } from '../../constants';
import { GeographicalZoneTemplate } from '../../constants/GeographicalZoneTemplate';
import { hasChildByName } from '../../helpers';
import { useApiCall } from '../../hooks/useApiCall';
import { RootState } from '../../state/store';
import {
  ComplexRoleUI,
  Employee,
  GeographicalZone,
  TreeNode
} from '../../types/interfaces';
import DetailedInformationCard from '../../ui/detailedInformationCard/DetailedInformationCard';
import EmployeeCard from '../../ui/EmployeeCard/EmployeeCard';
import { findNodeByKey } from '../../utils';
import { transformVisibilityArrayToMap } from '../../utils/rolesHelpters';

import { FunctionalStructureDialogs } from './FunctionalStructureDialogs';

const FunctionalStructure = () => {
  const treeRef = useRef<HTMLDivElement | null>(null);
  const [scrollHeight, setScrollHeight] = useState('100vh');
  const [deleteDialogVisible, setDeleteDialogVisible] =
    useState<boolean>(false);
  const lastConfirmedUrlRef = useRef<string>(window.location.href);
  const bypassNextNavigationRef = useRef<boolean>(false);
  const menuLeft = useRef<Menu>(null);
  const addEmployeeRef = useRef<null | { handleCancel: () => void }>(null);
  const geographicalZoneCardRef = useRef<null | { handleCancel: () => void }>(
    null
  );
  const [isGeographicalZoneDialogVisible, setIsGeographicalZoneDialogVisible] =
    useState<boolean>(false);
  const [selectedGeographicalZone, setSelectedGeographicalZone] =
    useState<GeographicalZone>(GeographicalZoneTemplate);
  const elementUIs = useSelector((state: RootState) => state.auth.elementUIs);
  const visibilityClasses = useMemo(
    () => transformVisibilityArrayToMap(elementUIs),
    [elementUIs]
  );
  const canEditGeographicalZones = Boolean(
    visibilityClasses['wholeui__roles'] ||
      visibilityClasses['geographicalZone-card__roles']
  );
  const canReadGeographicalZones = Boolean(
    canEditGeographicalZones ||
      visibilityClasses['geographicalZones-nav-btn__roles']
  );

  const {
    // Состояния
    treeNodes,
    selectedKey,
    expandedKeys,
    selectedItem,
    toast,
    items,
    hasUnsavedChanges,
    pendingActionRef,
    showUnsavedDialog,
    createOrganization,
    createDepartment,
    createEmployee,
    dialogVisible,
    detailedInformationCardProps,

    // Методы
    getDepartments,
    findParent,
    setSelectedKey,
    setExpandedKeys,
    setSelectedItem,
    runGuarded,
    setEmployeeCardDirty,
    setDetailedCardDirty,
    setAddEmployeeCardDirty,
    setShowUnsavedDialog,
    setCreateOrganization,
    setCreateDepartment,
    setCreateEmployee,
    handleUnsavedContinue,
    setDialogVisible,
    handleUnsavedCancel
  } = useFunctionalStructure();

  useApiCall(getDepartments);

  useEffect(() => {
    function offset(el: HTMLElement) {
      var rect = el.getBoundingClientRect(),
        scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      return { top: rect.top + scrollTop };
    }

    if (treeRef.current !== null) {
      const tableOffset = offset(treeRef.current).top;
      setScrollHeight(`calc(100vh - ${tableOffset + 65}px)`);
    }
  }, []);

  useEffect(() => {
    // Browser tab/window close protection (custom dialogs are blocked by browsers here)
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  useEffect(() => {
    // Guard internal SPA navigation without react-router blockers:
    // - intercept pushState/replaceState (react-router uses these for navigation)
    // - intercept popstate (back/forward)
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    const guardedHistoryFn =
      (originalFn: History['pushState'] | History['replaceState']) =>
      (...args: Parameters<History['pushState']>) => {
        if (bypassNextNavigationRef.current) {
          bypassNextNavigationRef.current = false;
          originalFn.apply(window.history, args as any);
          lastConfirmedUrlRef.current = window.location.href;
          return;
        }

        if (hasUnsavedChanges) {
          pendingActionRef.current = () => {
            bypassNextNavigationRef.current = true;
            originalFn.apply(window.history, args as any);
            lastConfirmedUrlRef.current = window.location.href;
          };
          setShowUnsavedDialog(true);
          return;
        }

        originalFn.apply(window.history, args as any);
        lastConfirmedUrlRef.current = window.location.href;
      };

    window.history.pushState = guardedHistoryFn(originalPushState);
    window.history.replaceState = guardedHistoryFn(originalReplaceState);

    const handlePopState = () => {
      if (!hasUnsavedChanges) {
        lastConfirmedUrlRef.current = window.location.href;
        return;
      }

      const targetUrl = window.location.href;
      const safeUrl = lastConfirmedUrlRef.current;

      // Immediately revert back to the last confirmed URL (keep UI stable)
      bypassNextNavigationRef.current = true;
      originalPushState.call(window.history, null, '', safeUrl);

      pendingActionRef.current = () => {
        bypassNextNavigationRef.current = true;
        originalPushState.call(window.history, null, '', targetUrl);
        lastConfirmedUrlRef.current = targetUrl;
      };

      setShowUnsavedDialog(true);
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
      window.removeEventListener('popstate', handlePopState);
    };
  }, [hasUnsavedChanges]);

  useEffect(() => {
    if (showUnsavedDialog) return;
    // If the right-side editor is not rendered, ensure dirty flags don't stick around.
    if (
      selectedItem === null &&
      !createOrganization &&
      !createEmployee &&
      !createDepartment
    ) {
      setEmployeeCardDirty(false);
      setDetailedCardDirty(false);
    }
  }, [
    selectedItem,
    createOrganization,
    createEmployee,
    createDepartment,
    showUnsavedDialog
  ]);

  useEffect(() => {
    const result = findNodeByKey(treeNodes, selectedKey);
    const namesRef = result?.label?.split(' ');
    if (result && namesRef && namesRef.length! > 2) {
      result.surname = namesRef[0];
      result.name = namesRef[1];
      result.middleName = namesRef[2];
    } else if (result && namesRef && namesRef.length! > 0) {
      result.surname = namesRef[0];
    }
    setCreateOrganization(false);
    setSelectedItem(result);
  }, [selectedKey, treeNodes]);

  const handleDeleteOnClick = async () => {
    if (selectedKey) {
      if (selectedItem?.data === 'Сотрудник')
        await EmployeesService.removeFromDepartment(
          [parseInt(selectedKey)],
          parseInt(findParent(treeNodes, selectedKey)?.node?.key ?? '0')
        );
      else await DepartmentsService.deleteDepartment(parseInt(selectedKey));
      await getDepartments();
      setSelectedKey('');
      if (toast.current) {
        toast.current.show({
          severity: 'warn',
          summary: 'Удаление',
          detail: `Произведено удаление ${
            selectedItem?.data === 'Участок' ? 'Участк' : selectedItem?.data
          } ${selectedItem?.label}`,
          life: 3000
        });
      }
    } else {
      console.log('No node is currently selected');
    }
  };

  const selectedEmployees = useMemo<Employee>(() => {
    const isUser: Employee = { ...UsersTemplate };
    if (selectedItem?.data === 'Сотрудник' && selectedItem !== null) {
      const unit = findNodeByKey(
        treeNodes,
        findParent(treeNodes, selectedItem.key).node?.key ?? ''
      );
      isUser.id = !createEmployee
        ? selectedItem.key.split('department')[0] ?? `${Date.now()}`
        : 0;
      isUser.key = selectedItem.key.split('department')[0];
      isUser.label = !createEmployee ? selectedItem.fullName ?? '' : '';
      isUser.surname = !createEmployee ? selectedItem.surname ?? '' : '';
      isUser.name = !createEmployee ? selectedItem.name ?? '' : '';
      isUser.middleName = !createEmployee ? selectedItem.middleName ?? '' : '';
      isUser.department = !createEmployee && unit ? [unit] : [];
    } else if (selectedItem !== null) {
      isUser.departmentIds = [parseInt(selectedItem.key)];
    }
    return isUser;
  }, [selectedItem, treeNodes, createEmployee]);

  useEffect(() => {
    setCreateDepartment(false);
    setCreateEmployee(false);
    if (selectedKey && !expandedKeys[selectedKey]) {
      setExpandedKeys((prev) => ({
        ...prev,
        [selectedKey]: true
      }));
    }
  }, [selectedKey]);

  const addElInTree = async (selection: ComplexRoleUI[] | Employee[]) => {
    await getDepartments();

    // Если родитель был закрыт - раскрываем его
    if (selectedItem?.key) {
      setExpandedKeys((prev) => ({
        ...prev,
        [selectedItem.key]: true // Раскрываем родительский узел
      }));
    }
  };

  useEffect(() => {
    if (selectedKey && treeNodes.length > 0) {
      const findAndExpandParents = (
        nodes: TreeNode[],
        targetKey: string,
        path: string[] = []
      ): string[] => {
        for (const node of nodes) {
          if (node.key === targetKey) {
            return path;
          }
          if (node.children) {
            const result = findAndExpandParents(node.children, targetKey, [
              ...path,
              node.key
            ]);
            if (result.length > 0) {
              return result;
            }
          }
        }
        return [];
      };

      const parentsToExpand = findAndExpandParents(treeNodes, selectedKey);

      if (parentsToExpand.length > 0) {
        setExpandedKeys((prev) => {
          const newKeys = { ...prev };
          parentsToExpand.forEach((parentKey) => {
            newKeys[parentKey] = true;
          });
          return newKeys;
        });
      }
    }
  }, [selectedKey, treeNodes]);

  const onUpdate = async (key: string | number, updatedEmployee: Employee) => {
    const keyString = typeof key === 'string' ? key : key.toString();
    await getDepartments();
    const updatedNode = findNodeByKey(treeNodes, keyString);
    if (updatedNode) {
      setSelectedItem(updatedNode);
    }
  };

  const handleAdd = async (employee: Employee) => {
    await getDepartments();
    const newSelectedKey = `${employee.id}department${selectedKey}`;
    setSelectedKey(newSelectedKey);
    setCreateEmployee(false);
  };

  const onHideEmployeeCard = () => {
    if (addEmployeeRef.current !== null) {
      addEmployeeRef.current.handleCancel();
    }
  };

  const handleDeleteConfirmation = () => {
    handleDeleteOnClick();
    setDeleteDialogVisible(false);
  };

  const handleGeographicalZoneUpdate = useCallback(
    async (id: number, updatedObject: GeographicalZone) => {
      const result = await GeographicalZoneService.updateGeographicalZone(
        id,
        updatedObject
      );
      await getDepartments();
      return result;
    },
    [getDepartments]
  );

  const handleGeographicalZoneDelete = useCallback(
    async (id: number | string) => {
      await GeographicalZoneService.deleteGeographicalZone(Number(id));
      await getDepartments();
      setIsGeographicalZoneDialogVisible(false);
    },
    [getDepartments]
  );

  const openGeographicalZoneCard = useCallback(
    async (zoneId: string | number) => {
      if (!canReadGeographicalZones) {
        if (toast.current) {
          toast.current.show({
            severity: 'warn',
            summary: 'Ошибка',
            detail: 'Недостаточно прав для просмотра географической зоны',
            life: 3000
          });
        }
        return;
      }

      const parsedId = Number(zoneId);
      if (!parsedId) return;
      const response =
        await GeographicalZoneService.getGeographicalZoneById(parsedId);
      if (response?.data) {
        setSelectedGeographicalZone(response.data);
        setIsGeographicalZoneDialogVisible(true);
      }
    },
    [canReadGeographicalZones]
  );

  const onHideGeographicalZoneCard = () => {
    if (geographicalZoneCardRef.current !== null) {
      geographicalZoneCardRef.current.handleCancel();
    }
  };

  const onNodeClick = (event: any) => {
    const node = event.node;
    const nodeKey = node.key;
    if (nodeKey !== selectedKey) return;
    if (node.children && node.children.length > 0) {
      setExpandedKeys((prev) => {
        const newExpandedKeys = { ...prev };
        if (newExpandedKeys[nodeKey]) {
          delete newExpandedKeys[nodeKey];
        } else {
          newExpandedKeys[nodeKey] = true;
        }
        return newExpandedKeys;
      });
    }

    if (createDepartment || createEmployee) {
      runGuarded(() => {
        setCreateDepartment(false);
        setCreateEmployee(false);
      });
    }
  };
  const handleToggle = (event: TreeExpandedEvent) => {
    setExpandedKeys(event.value as { [key: string]: boolean });
  };

  const nodeTemplate = (node: any) => {
    let iconClass = '';
    let iconColor = '';
    
    if (node.data === 'Сотрудник') {
      iconClass = 'pi pi-user';
      iconColor = '#4a90e2';
    } else {
      iconClass = 'pi pi-building';
      iconColor = '#4a90e2';
    }
    
    return (
      <div className="flex align-items-center gap-2">
        {iconClass && (
          <i 
            className={iconClass} 
            style={{ 
              color: iconColor,
              fontSize: '1.1rem',
              marginRight: '0.5rem'
            }} 
          />
        )}
        <span>{node.label}</span>
      </div>
    );
  };

  return (
    <div className="functional-structure--wrapper">
      <div className="functional-structure wrapper wrapper--main formgrid grid w-full">
        <div
          ref={treeRef}
          className="field col-4 xxl:col-5 functional-structure--tree"
        >
          <Panel>
            <Toast ref={toast} />
            <div className="flex w-full">
              <div className="tree-card__buttons mb-3">
                <div className="flex flex-column gap-3">
                  <Menu
                    model={items}
                    popup
                    ref={menuLeft}
                    id="popup_menu_left"
                    popupAlignment="left"
                    style={{ width: '250px' }}
                  />
                  <Button
                    tooltip="Создать"
                    severity="success"
                    rounded
                    icon="pi pi-plus"
                    className="mr-3 functional-structure-add-btn__roles"
                    onClick={(event) => menuLeft.current?.toggle(event)}
                    aria-controls="popup_menu_left"
                    aria-haspopup
                  />
                  <Button
                    tooltip="Удалить"
                    icon="pi pi-trash"
                    onClick={() => {
                      runGuarded(() => {
                        if (selectedItem) setDeleteDialogVisible(true);
                      });
                    }}
                    className="p-button-danger mr-3 functional-structure-delete-btn__roles"
                    rounded
                  />
                </div>
              </div>
              <Tree
                style={{ height: scrollHeight }}
                value={treeNodes}
                selectionMode="single"
                selectionKeys={selectedKey}
                expandedKeys={expandedKeys}
                onToggle={handleToggle}
                onNodeClick={onNodeClick}
                onSelectionChange={(e) => {
                  if (typeof e.value === 'string') {
                    const nextKey = e.value;
                    if (nextKey === selectedKey) return;
                    runGuarded(() => {
                      setSelectedKey(nextKey);
                      setCreateDepartment(false);
                      setCreateEmployee(false);
                    });
                  }
                }}
                filter
                filterMode="lenient"
                filterPlaceholder="Поиск по дереву"
                className="my-tree w-full"
                nodeTemplate={nodeTemplate}
              />
            </div>
          </Panel>
        </div>
        <div className="field col-8">
          <Panel
            header="Детальная информация "
            className="detailInfo-functional-structure"
          >
            {(selectedItem?.data === 'Сотрудник' || createEmployee) &&
              !createDepartment &&
              !createOrganization && (
                <EmployeeCard
                  functionalStructure={true}
                  key={selectedEmployees.id}
                  employeeProps={selectedEmployees}
                  onDirtyChange={setEmployeeCardDirty}
                  onAdd={handleAdd}
                  onUpdate={onUpdate}
                  withoutButtons={true}
                  withoutDialog={true}
                  showToastOnSave
                />
              )}
            {!createEmployee &&
              (createDepartment ||
                createOrganization ||
                (selectedItem?.data && !selectedItem?.isPerson)) && (
                <DetailedInformationCard
                  {...detailedInformationCardProps}
                  key={detailedInformationCardProps.key}
                  onDirtyChange={setDetailedCardDirty}
                  onGeographicalZoneClick={openGeographicalZoneCard}
                />
              )}
            {selectedItem === null && !selectedKey && !createOrganization && (
              <div className="pt-3">
                Выберите элемент в дереве для отображения информации.
              </div>
            )}
          </Panel>
        </div>
      </div>
      <FunctionalStructureDialogs
        dialogVisible={dialogVisible}
        onHideEmployeeCard={onHideEmployeeCard}
        addedEmployees={
          selectedItem?.data === 'Участок' &&
          selectedItem &&
          hasChildByName(selectedItem, 'Сотрудник')
            ? selectedItem.children ?? []
            : [selectedEmployees]
        }
        employeeCardKey={selectedEmployees.id}
        onDirtyChange={setAddEmployeeCardDirty}
        updateTree={addElInTree}
        closeEmployeeDialog={() => {
          setDialogVisible(false);
          setAddEmployeeCardDirty(false);
        }}
        addEmployeeCardRef={addEmployeeRef}
        parentKey={selectedItem?.key}
        isGeographicalZoneDialogVisible={isGeographicalZoneDialogVisible}
        onHideGeographicalZoneCard={onHideGeographicalZoneCard}
        selectedGeographicalZone={selectedGeographicalZone}
        onGeographicalZoneUpdate={handleGeographicalZoneUpdate}
        onGeographicalZoneDelete={handleGeographicalZoneDelete}
        closeGeographicalZoneDialog={() =>
          setIsGeographicalZoneDialogVisible(false)
        }
        geographicalZoneCardRef={geographicalZoneCardRef}
        geographicalZoneReadOnly={!canEditGeographicalZones}
        deleteDialogVisible={deleteDialogVisible}
        onHideDeleteDialog={() => setDeleteDialogVisible(false)}
        onConfirmDelete={handleDeleteConfirmation}
        selectedItem={selectedItem}
        showUnsavedDialog={showUnsavedDialog}
        onUnsavedCancel={handleUnsavedCancel}
        onUnsavedContinue={handleUnsavedContinue}
      />
    </div>
  );
};
export default FunctionalStructure;
